<template>
  <div
    ref="scroll_container "
    data-tst="study_action_btns"
    class="sab_container no-scroll-track"
    @wheel.stop="e => wheelScroll(e)"
    @touchend="hscrl_handleTouchend"
  >
    <div
      v-if="showActions"
      class="sab no-scroll-track"
      :style="styleVars"
    >
      <div
        v-for="(action, idx) in groupingSolution"
        :key="`${action.code}_${idx}`"
        v-ripple
        class="sab_btn"
        :data-tst="`action_${action.code}_${idx}`"
        :class="[
          style.css_class,
          {
            sab_btn_active:
              useHex && action.group_id
                ? action.selected
                : filterActions.some(i => i.code === action.code),
          },
          {
            'gw_hvr_pointer_coarse_disabled no-events opacity_4': actionIsDisabled(action),
          },
        ]"
        @click="
          toggleAction(
            action,
            filterActions.some(i => i.code === action.code),
          )
        "
        @mouseenter="handleMouseenter(action)"
        @mouseleave="handleMouseleave"
      >
        <div
          class="sab_btn_back"
          :class="action.css_class"
          :style="useHex ? `background:${action.hex}` : ''"
        >
          <div class="sab_btn_back_overlay"></div>
        </div>

        <GwMenu
          v-if="hasBreakdown && !isPreflop && action.total_combos > 0.001"
          ref="gwmenu"
          :flOptions="{
            placement: 'bottom',
            offset: -10,
          }"
          style="position: absolute; inset: 0 0 auto auto; z-index: 2"
          trigger="hover"
          @click.stop
        >
          <template #opener>
            <div
              class="sab_btn_breakdown"
              @click.stop
            >
              <i class="mdi mdi-chart-box-outline sab_btn_breakdown_icon"></i>
            </div>
          </template>
          <div
            class="gw_menu"
            style="--width: auto; padding: 0.75rem 0 1rem 0"
          >
            <StudyComboBreakdown
              :action="action"
              :catDraws="catDraws"
              :catHands="catHands"
              data-tst="action_breakdown"
            />
          </div>
        </GwMenu>

        <div class="sab_btn_name-row">
          <div class="sab_btn_name gw_hvr">
            <div
              v-if="action.group_id"
              class="position-relative"
            >
              <div
                class="truncate csst csst_nm"
                @mouseenter="e => fl_positionTooltip(e, { placement: 'bottom' })"
              >
                {{ $t(`browse.${action.group_display_name}`) }}
                <div
                  class="csst_content"
                  style="position: fixed"
                  @click.stop
                >
                  {{ $t(`browse.${action.group_id}_help`) }}
                </div>
              </div>
            </div>
            <div
              v-else
              class="sab_btn_name_bet"
            >
              <div class="sab_btn_name_bet_big">
                {{ action.display_name }}
                {{
                  showBetsIn === 'pot' && !isPreflop && action.amount_by_pot
                    ? `${action.amount_by_pot}%`
                    : showBetsIn === 'pot_and_bb' && !isPreflop && action.amount_by_pot
                      ? `${action.amount_by_pot}%`
                      : action.raise_amount
                }}
              </div>
              <div
                v-if="showBetsIn === 'bb_and_pot' && action.amount_by_pot"
                class="sab_btn_name_bet_small"
              >
                ({{ action.amount_by_pot }}%)
              </div>
              <div
                v-if="showBetsIn === 'pot_and_bb' && action.amount_by_pot"
                class="sab_btn_name_bet_small"
              >
                ({{ action.raise_amount }})
              </div>
            </div>
            <div
              v-if="hasFrequency"
              class="sab_btn_fre mt-auto"
            >
              {{
                actionIsDisabled(action)
                  ? '-'
                  : numFixedUnit(action[freKey] * 100, 'strategy') + '%'
              }}
            </div>
          </div>
          <div
            v-if="hasCombos"
            class="sab_btn_combos text-right"
          >
            {{ numFixed(action.total_combos, 2) }}
            <div class="sab_btn_combos_title">combos</div>
          </div>
        </div>
      </div>
    </div>

    <StudyFrequencyChart
      v-else
      :hasBreakdown="hasBreakdown && showFlopContent"
      :actions="groupingSolution"
      :showBetsIn="showBetsIn"
      :filterActions="filterActions"
      :freKey="freKey"
      :catDraws="catDraws"
      :catHands="catHands"
      @action:click="
        action =>
          toggleAction(
            action,
            filterActions.some(i => i.code === action.code),
          )
      "
    >
    </StudyFrequencyChart>
  </div>
</template>

<script>
import GwMenu from '@/components/lib/GwMenu'
import StudyComboBreakdown from '@/components/Study/StudyComboBreakdown'
import StudyFrequencyChart from '@/components/Charts/BarChart/StudyFrequencyChart'
import horizontalScroll_mix from '@/components/HorizolnalScroller/horizontalScroll_mix'
import format_mix from '@/mixins/format_mix'
import grouping_mix from '@/components/Study/grouping_mix'
import float_mix from '@/mixins/float_mix'

export default {
  name: 'StudyActionBtns',
  components: {
    StudyComboBreakdown,
    StudyFrequencyChart,
    GwMenu,
  },
  mixins: [format_mix, horizontalScroll_mix, grouping_mix, float_mix],
  props: {
    customSolution: Array,
    freKey: { default: () => 'total_frequency' },
    hasFrequency: { type: Boolean, default: true },
    hasCombos: { type: Boolean, default: true },
    maxRows: { default: () => 3 },
    showActions: { default: () => true },
    showBetsIn: String,
    grouping: String,
    useHex: Boolean,
    isPreflop: Boolean,
    showFlopContent: Boolean,
    filterActions: Array,
    catDraws: Array,
    catHands: Array,
    hasBreakdown: { type: Boolean, default: true },
    applyFilterActionName: { default: () => 'sol_data_applyFilter' },
    hasDisabledZeroFreqActions: Boolean,
  },
  data() {
    return {
      breakdownActionCode: '',
    }
  },
  methods: {
    handleMouseenter(action) {
      // this.breakdownActionCode = action.code
      this.$store.commit('sol_filters_set', { key: 'actions_highlighted', value: [action] })
    },
    handleMouseleave() {
      this.$store.commit('sol_filters_set', { key: 'actions_highlighted', value: [] })
    },
    actionIsDisabled(action) {
      return this.hasDisabledZeroFreqActions && !action.frequency
    },
  },
  computed: {
    styleVars() {
      return {
        '--sab-columns': `repeat(${this.style.col}, 1fr)`,
        '--sab-border-width': `${this.style.bw}px`,
        '--sab-font-size': `${this.style.fname || 1}rem`,
        '--sab-fre-font-size': `${this.style.ffre || this.style.fname || 1}rem`,
      }
    },
    style() {
      const { length } = this.groupingSolution
      switch (length) {
        case 1:
          return { col: 1, fname: 1.75, ffre: 2.75, bw: 3, css_class: 'sab_btn_big' }
        case 2:
          return { col: 2, fname: 1.75, ffre: 2.75, bw: 3, css_class: 'sab_btn_big' }
        case 3:
          return { col: 3, fname: 1.75, ffre: 2, bw: 3, css_class: 'sab_btn_bet_column' }
        case 4:
          return this.$store.getters.screen_max1540
            ? { col: 2, fname: 1.1, ffre: 1.5, bw: 3 }
            : { col: 4, fname: 1.5, ffre: 2, bw: 3, css_class: 'sab_btn_bet_column' }
        case 5:
          return { col: 3, fname: 1.1, ffre: 1.5, bw: 3 }
        case 6:
          return { col: 3, fname: 1.1, ffre: 1.5, bw: 3 }
        case 7:
          return { col: 4, fname: 1.1, bw: 2 }
        case 8:
          return { col: 4, fname: 1.1, bw: 2 }
        case 9:
          return { col: 5, fname: 1.1, bw: 2, css_class: 'sab_btn_small' }
        case 10:
          return { col: 5, fname: 1.1, bw: 2, css_class: 'sab_btn_small' }
        case 11:
          return { col: 6, fname: 1, bw: 2, css_class: 'sab_btn_small' }
        case 12:
          return { col: 6, fname: 1, bw: 2, css_class: 'sab_btn_small' }
        default:
          return {
            col: Math.ceil(length / this.maxRows),
            fname: 0.875,
            ffre: 0.875,
            // fcombo: 0.875,
            bw: 2,
            css_class: 'sab_btn_small',
          }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
//  > 21 actions
// http://localhost:8080/solutions?gametype=Cash6m50zComplex&depth=100&history_spot=6&soltab=strategy&gmff_type=complex&gmfft_sort_key=0&gmfft_sort_order=desc&gmff_rake=NL50&preflop_actions=F-F-F-R2.5-F-C&board=QsTs7h
.sab_container {
  --min-height: 0;
  min-height: var(--min-height);
  flex: 1;
  position: relative;
  display: flex;
}
.sab {
  grid-template-columns: var(--sab-columns);
  flex-shrink: 0;
  flex: 1;
  position: relative;
  display: grid;
  flex-wrap: wrap;
  grid-gap: 4px;
  &_btn {
    color: var(--clr-action_text);
    text-shadow: var(--action-btn-text-shadow);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    position: relative;
    white-space: nowrap;
    font-size: 1rem;
    font-size: var(--sab-font-size);
    background: var(--clr-black-0);

    &_active {
      .sab_btn_back {
        &_overlay {
          background: rgba(0, 0, 0, 0.5);
        }
      }
    }
    &_back {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      border-radius: 4px;
      z-index: 0;
      border: solid var(--sab-border-width) transparent;
      &_overlay {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        border-radius: 3px;
        z-index: 0;
        transition: 0.2s;
      }
    }
    &_breakdown {
      z-index: 2;
      position: absolute;
      right: 0;
      top: 0;
      width: 2rem;
      height: 2rem;
      &_icon {
        position: absolute;
        right: 0.25rem;
        opacity: 0.7;
        font-size: 1.125rem;
      }
    }
    &_small {
      padding: 0 0.5rem;
      .sab_btn_name {
        padding: 0.25rem 0;
        &_bet {
          position: relative;
          flex-wrap: nowrap;
        }
      }
      .sab_btn_fre {
        line-height: unset !important;
      }
      .sab_btn_combos {
        display: none;
      }
      .sab_btn_breakdown {
        .sab_btn_breakdown_icon {
          font-size: 0.875rem;
        }
      }
    }
    &_bet_column {
      .sab_btn_name {
        &_bet {
          flex-direction: column;
        }
      }
    }
    &_big {
      .sab_btn_name {
        &_bet {
          flex-direction: column;
        }
      }
      .sab_btn_fre {
        line-height: 1 !important;
      }
    }
    &_name {
      padding: 0 0.2rem 0 0;
      margin-right: auto;
      display: flex;
      flex-direction: column;
      &_bet {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        position: absolute;
        &_big {
          font-size: inherit;
          padding: 0 0.25rem 0 0;
        }
        &_small {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          line-height: 1;
        }
      }
    }
    &_fre {
      font-size: var(--sab-fre-font-size);
      line-height: 1.2;
    }
    &_combos {
      font-weight: 500;
      margin: auto 0 0 0;
      line-height: 1.2;
      font-size: clamp(0.75rem, calc(var(--sab-fre-font-size) * 0.55), 1rem);
      &_title {
        opacity: 0.8;
        font-size: 0.7rem;
      }
    }
    &_name-row {
      z-index: 1;
      font-weight: 500;
      position: relative;
      display: flex;
      flex: 1;
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .sab {
    &_btn {
      &:hover {
        z-index: 4;
        .sab_btn_back_overlay {
          transition: 0s;
          background: rgba(0, 0, 0, 0.5);
        }
      }
    }
  }
}
</style>
